import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import uuid from 'uuid';
import { ButtonText, Icon, ConfirmationModal, Body2 } from 'ensightful-koala-ui';

import { H3 } from 'components/atoms/Text';
import {
    CourseSetUpContainer,
    HeaderContainer,
    TeamSetUpTable,
    TeamSetUpModal,
} from 'components/organisms/CourseSetUp';
import { PrimaryButton } from 'components/molecules/Button';
import { InputWithBorder } from 'components/molecules/TextInput';

const StudentListContainer = styled.div`
    margin-top: 1.72em;
`;

const ToolsContainer = styled.div`
    display: ${(props) => (props.review ? 'none' : 'flex')};
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.85em;
`;

const ToolLeftContainer = styled.div`
    ${(props) => (props.device === 'mobile' ? 'width: 100%;' : '')}
`;

const ToolRightContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${(props) => (props.device === 'mobile' ? 'width: 100%;' : '')}
    ${(props) => (props.device === 'mobile' ? 'flex-wrap: wrap-reverse;' : '')}
`;

const ToolItemContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    & > div {
        margin-right: 8px;
    }
    &:hover {
        cursor: pointer;
    }
`;

const StyledButtonText = styled(ButtonText)`
    color: #757575;
    margin-left: 8px;
`;

const RandomizeModalContainer = styled.div``;

const StyledText = styled(Body2)`
    color: #616161;
`;

const ToolButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    ${(props) => (props.device === 'mobile' ? 'margin-top: 8px;' : '')}
    ${(props) => (props.device === 'mobile' ? 'justify-content: space-evenly;' : '')}
    ${(props) => (props.device === 'mobile' ? 'width: 100%;' : '')}
`;

const TeamSetUp = ({ fieldKeys, courseData = {}, device = 'desktop', review = false, options, handleInputChange }) => {
    const [teamList, setTeamList] = useState([]);
    const [currentTeamData, setCurrentTeamData] = useState();
    const [isTeamDetailModalVisible, setIsTeamDetailModalVisible] = useState(false);
    const [isRandomizeModalVisible, setIsRandomizeModalVisible] = useState(false);
    const [isRemoveTeamModalVisible, setIsRemoveTeamModalVisible] = useState(false);
    const [removeTeamId, setRemoveTeamId] = useState(false);
    const [teamSearch, setTeamSearch] = useState('');

    const handleEditTeamButtonClick = (id) => {
        const findCurrentTeam = teamList.find((team) => team.id === id);
        setCurrentTeamData(findCurrentTeam);
        return setIsTeamDetailModalVisible(true);
    };

    const handleTeamClick = (id) => {
        const findCurrentTeam = teamList.find((team) => team.id === id);
        return setCurrentTeamData(findCurrentTeam);
    };

    const handleAddTeam = () => {
        setTeamList((prev) => [
            ...prev,
            { id: uuid.v4(), name: `Team ${teamList?.length + 1}`, members: [], number: teamList?.length + 1 },
        ]);
    };

    const handleRemoveMember = (id) => {
        const updatedCurrentTeamData = {
            ...currentTeamData,
            members: currentTeamData.members.filter((member) => member.id !== id),
        };
        setCurrentTeamData(updatedCurrentTeamData);
        setTeamList((prev) => prev.map((team) => (team.id === currentTeamData.id ? updatedCurrentTeamData : team)));
        const newStudentData = courseData[fieldKeys.STUDENT_LIST]?.map((student) => {
            if (id === student.id) {
                const { group, ...rest } = student;
                return rest;
            }
            return student;
        });
        return handleInputChange(fieldKeys.STUDENT_LIST, newStudentData);
    };

    const teamListGenerator = () => {
        const currentStudentList = courseData[fieldKeys.STUDENT_LIST] || [];
        const reducedTeamList = currentStudentList.reduce((studentList, student) => {
            if (student?.group in studentList) {
                studentList[student.group] = [...studentList[student.group], student];
            } else if (student?.group) {
                studentList[student.group] = [student];
            }
            return studentList;
        }, {});
        let newTeamList = [];
        Object.entries(reducedTeamList).forEach(([key, value], index) => {
            if (key) {
                let groupName = `Team ${index + 1}`;
                if (value?.length > 0 && value[0]?.group_name) groupName = value[0].group_name;
                newTeamList = [...newTeamList, { id: uuid.v4(), name: groupName, members: value, number: key }];
            }
        });
        setTeamList(newTeamList);
        return handleInputChange(fieldKeys.TEAM_LIST, newTeamList);
    };

    const handleReset = () => {
        return handleInputChange(
            fieldKeys.STUDENT_LIST,
            courseData[fieldKeys.STUDENT_LIST].map((student) => ({ ...student, group: null }))
        );
    };

    const handleRandomize = () => {
        if (teamList?.length <= 0) return setIsRandomizeModalVisible(false);
        const unassignedStudents = courseData[fieldKeys.STUDENT_LIST]?.filter((student) => !student.group);
        const maxNum = teamList.length;
        let counter = 1;
        const updatedStduentData = courseData[fieldKeys.STUDENT_LIST].map((student) => {
            if (unassignedStudents.some((unassignedStudent) => unassignedStudent.id === student.id)) {
                student.group = counter;
                counter++;
                if (counter > maxNum) counter = 1;
            }
            return student;
        });
        handleInputChange(fieldKeys.STUDENT_LIST, updatedStduentData);
        return setIsRandomizeModalVisible(false);
    };

    const handleCurrentTeamData = (data) => {
        setCurrentTeamData(data);
        const newStudentData = courseData[fieldKeys.STUDENT_LIST]?.map((student) => {
            if (data?.members?.some((member) => member.id === student.id)) {
                student.group = data.number;
                return student;
            }
            return student;
        });
        return handleInputChange(fieldKeys.STUDENT_LIST, newStudentData);
    };

    const handleRemoveTeam = (id) => {
        const findRemovingTeam = teamList.find((team) => team.id === id);
        if (!findRemovingTeam) return;
        const newTeamList = teamList.filter((team) => team.id !== id);
        const newStudentData = courseData[fieldKeys.STUDENT_LIST]?.map((student) => {
            if (Number(findRemovingTeam.number) === Number(student.group)) {
                const { group, groupName, group_name, ...rest } = student;
                return rest;
            }
            return student;
        });
        handleInputChange(fieldKeys.STUDENT_LIST, newStudentData);
        setIsTeamDetailModalVisible(false);
        setIsRemoveTeamModalVisible(false);
        return setTeamList(newTeamList);
    };

    const handleRemoveTeamModal = (id) => {
        setRemoveTeamId(id);
        setIsRemoveTeamModalVisible(true);
    };

    useEffect(() => {
        teamListGenerator();
    }, [courseData[fieldKeys.STUDENT_LIST]]);

    useEffect(() => {
        if (currentTeamData) {
            const newTeamList = teamList.map((team) => (team.id === currentTeamData.id ? currentTeamData : team));
            setTeamList(newTeamList);
        }
    }, [currentTeamData]);

    return (
        <CourseSetUpContainer>
            <HeaderContainer review={review}>
                <H3>Set-up Teams</H3>
            </HeaderContainer>
            <ToolsContainer review={review}>
                <ToolLeftContainer device={device}>
                    <InputWithBorder
                        error={false}
                        errorMessage=''
                        placeholder='Search'
                        required={false}
                        title=''
                        value={teamSearch}
                        onChange={(value) => setTeamSearch(value)}
                        style={{ width: '100%' }}
                    />
                </ToolLeftContainer>
                <ToolRightContainer device={device}>
                    <ToolButtonContainer device={device}>
                        <ToolItemContainer>
                            <Icon kind={'reset'} fill={'#757575'} />
                            <StyledButtonText onClick={() => handleReset()}> Reset </StyledButtonText>
                        </ToolItemContainer>
                        <ToolItemContainer style={{ margin: '0 1.5em 0 2em' }}>
                            <Icon kind={'shuffle'} fill={'#757575'} size={16} />
                            <StyledButtonText onClick={() => setIsRandomizeModalVisible(true)}>
                                {' '}
                                Randomize{' '}
                            </StyledButtonText>
                        </ToolItemContainer>
                    </ToolButtonContainer>
                    <PrimaryButton
                        style={{ width: device === 'mobile' ? '100%' : 'fit-content' }}
                        onClick={handleAddTeam}
                        text={'+ Add Item'}
                        active={true}
                    />
                </ToolRightContainer>
            </ToolsContainer>

            <StudentListContainer>
                <TeamSetUpTable
                    type={fieldKeys?.STUDENT_LIST}
                    teamList={teamList.filter(
                        (team) => team?.name?.toLowerCase()?.includes(teamSearch?.toLowerCase()) || teamSearch === ''
                    )}
                    handleInputChange={handleInputChange}
                    review={review}
                    isWithTeam={false}
                    handleEditTeamButtonClick={handleEditTeamButtonClick}
                />
            </StudentListContainer>
            <TeamSetUpModal
                isVisible={isTeamDetailModalVisible}
                closable={true}
                onCancel={() => setIsTeamDetailModalVisible(false)}
                studentList={courseData[fieldKeys.STUDENT_LIST]}
                teamList={teamList}
                currentTeamData={currentTeamData}
                device={device}
                handleCurrentTeamData={(data) => handleCurrentTeamData(data)}
                handleTeamClick={handleTeamClick}
                handleAddTeam={handleAddTeam}
                handleRemoveMember={handleRemoveMember}
                handleRemoveTeam={(id) => handleRemoveTeamModal(id)}
            />
            <ConfirmationModal
                visible={isRandomizeModalVisible}
                title={'Randomize Teams?'}
                content={'All unassigned students will be randomly placed in a team. This cannot be undone.'}
                confirmText={'Randomize'}
                cancelText={'Cancel'}
                colorType={'primary'}
                onCancel={() => setIsRandomizeModalVisible(false)}
                onConfirm={handleRandomize}
            />
            <ConfirmationModal
                visible={isRemoveTeamModalVisible}
                title={'Delete Team?'}
                content={'All students assigned will be removed from the team.'}
                confirmText={'Delete'}
                cancelText={'Cancel'}
                colorType={'warning'}
                onCancel={() => setIsRemoveTeamModalVisible(false)}
                onConfirm={() => handleRemoveTeam(removeTeamId)}
            />
        </CourseSetUpContainer>
    );
};

export default TeamSetUp;
